import React, { useState } from 'react';
import { RegistrationFormData } from '../types/registration';
import { t, getLang } from '../services/translation';

interface Step2Props {
  data: Pick<RegistrationFormData, 'lastName' | 'firstName' | 'phone' | 'birthYear' | 'gender' | 'languagePreference'>;
  onChange: (fields: Partial<RegistrationFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export interface CountryCode {
  code: string;
  nameHu: string;
  nameEn: string;
  flag: string;
  placeholder: string;
}

export const COUNTRY_CODES: CountryCode[] = [
  // Kiemelt / Magyarország
  { code: '+36', nameHu: 'Magyarország', nameEn: 'Hungary', flag: '🇭🇺', placeholder: '20 123 4567' },

  // Szomszédos és közeli országok
  { code: '+40', nameHu: 'Románia', nameEn: 'Romania', flag: '🇷🇴', placeholder: '712 345 678' },
  { code: '+421', nameHu: 'Szlovákia', nameEn: 'Slovakia', flag: '🇸🇰', placeholder: '912 345 678' },
  { code: '+43', nameHu: 'Ausztria', nameEn: 'Austria', flag: '🇦🇹', placeholder: '664 1234567' },
  { code: '+380', nameHu: 'Ukrajna', nameEn: 'Ukraine', flag: '🇺🇦', placeholder: '50 123 4567' },
  { code: '+381', nameHu: 'Szerbia', nameEn: 'Serbia', flag: '🇷🇸', placeholder: '60 1234567' },
  { code: '+385', nameHu: 'Horvátország', nameEn: 'Croatia', flag: '🇭🇷', placeholder: '91 234 5678' },
  { code: '+386', nameHu: 'Szlovénia', nameEn: 'Slovenia', flag: '🇸🇮', placeholder: '31 234 567' },
  { code: '+420', nameHu: 'Csehország', nameEn: 'Czech Republic', flag: '🇨🇿', placeholder: '601 123 456' },
  { code: '+48', nameHu: 'Lengyelország', nameEn: 'Poland', flag: '🇵🇱', placeholder: '512 345 678' },

  // Nyugat-, Észak- és Dél-Európa
  { code: '+49', nameHu: 'Németország', nameEn: 'Germany', flag: '🇩🇪', placeholder: '170 1234567' },
  { code: '+44', nameHu: 'Egyesült Királyság', nameEn: 'United Kingdom', flag: '🇬🇧', placeholder: '7123 456789' },
  { code: '+33', nameHu: 'Franciaország', nameEn: 'France', flag: '🇫🇷', placeholder: '6 12 34 56 78' },
  { code: '+39', nameHu: 'Olaszország', nameEn: 'Italy', flag: '🇮🇹', placeholder: '312 345 6789' },
  { code: '+34', nameHu: 'Spanyolország', nameEn: 'Spain', flag: '🇪🇸', placeholder: '612 345 678' },
  { code: '+31', nameHu: 'Hollandia', nameEn: 'Netherlands', flag: '🇳🇱', placeholder: '6 12345678' },
  { code: '+32', nameHu: 'Belgium', nameEn: 'Belgium', flag: '🇧🇪', placeholder: '470 12 34 56' },
  { code: '+41', nameHu: 'Svájc', nameEn: 'Switzerland', flag: '🇨🇭', placeholder: '78 123 45 67' },
  { code: '+351', nameHu: 'Portugália', nameEn: 'Portugal', flag: '🇵🇹', placeholder: '912 345 678' },
  { code: '+30', nameHu: 'Görögország', nameEn: 'Greece', flag: '🇬🇷', placeholder: '69 1234 5678' },
  { code: '+353', nameHu: 'Írország', nameEn: 'Ireland', flag: '🇮🇪', placeholder: '85 123 4567' },
  { code: '+46', nameHu: 'Svédország', nameEn: 'Sweden', flag: '🇸🇪', placeholder: '70 123 45 67' },
  { code: '+47', nameHu: 'Norvégia', nameEn: 'Norway', flag: '🇳🇴', placeholder: '412 34 567' },
  { code: '+45', nameHu: 'Dánia', nameEn: 'Denmark', flag: '🇩🇰', placeholder: '20 12 34 56' },
  { code: '+358', nameHu: 'Finnország', nameEn: 'Finland', flag: '🇫🇮', placeholder: '41 234 5678' },
  { code: '+359', nameHu: 'Bulgária', nameEn: 'Bulgaria', flag: '🇧🇬', placeholder: '87 123 4567' },
  { code: '+357', nameHu: 'Ciprus', nameEn: 'Cyprus', flag: '🇨🇾', placeholder: '96 123456' },
  { code: '+370', nameHu: 'Litvánia', nameEn: 'Lithuania', flag: '🇱🇹', placeholder: '612 34567' },
  { code: '+371', nameHu: 'Lettország', nameEn: 'Latvia', flag: '🇱🇻', placeholder: '21 234 567' },
  { code: '+372', nameHu: 'Észtország', nameEn: 'Estonia', flag: '🇪🇪', placeholder: '5123 4567' },
  { code: '+387', nameHu: 'Bosznia-Hercegovina', nameEn: 'Bosnia & Herzegovina', flag: '🇧🇦', placeholder: '61 123 456' },
  { code: '+382', nameHu: 'Montenegró', nameEn: 'Montenegro', flag: '🇲🇪', placeholder: '67 123 456' },
  { code: '+389', nameHu: 'Észak-Macedónia', nameEn: 'North Macedonia', flag: '🇲🇰', placeholder: '70 123 456' },
  { code: '+355', nameHu: 'Albánia', nameEn: 'Albania', flag: '🇦🇱', placeholder: '67 123 4567' },
  { code: '+373', nameHu: 'Moldova', nameEn: 'Moldova', flag: '🇲🇩', placeholder: '621 12 345' },

  // Ázsia (3. országbeli diákok és munkavállalók)
  { code: '+86', nameHu: 'Kína', nameEn: 'China', flag: '🇨🇳', placeholder: '138 1234 5678' },
  { code: '+91', nameHu: 'India', nameEn: 'India', flag: '🇮🇳', placeholder: '98765 43210' },
  { code: '+84', nameHu: 'Vietnám', nameEn: 'Vietnam', flag: '🇻🇳', placeholder: '91 234 5678' },
  { code: '+82', nameHu: 'Dél-Korea', nameEn: 'South Korea', flag: '🇰🇷', placeholder: '10 1234 5678' },
  { code: '+63', nameHu: 'Fülöp-szigetek', nameEn: 'Philippines', flag: '🇵🇭', placeholder: '917 123 4567' },
  { code: '+81', nameHu: 'Japán', nameEn: 'Japan', flag: '🇯🇵', placeholder: '90 1234 5678' },
  { code: '+92', nameHu: 'Pakisztán', nameEn: 'Pakistan', flag: '🇵🇰', placeholder: '300 1234567' },
  { code: '+880', nameHu: 'Banglades', nameEn: 'Bangladesh', flag: '🇧🇩', placeholder: '1712 345678' },
  { code: '+62', nameHu: 'Indonézia', nameEn: 'Indonesia', flag: '🇮🇩', placeholder: '812 3456 7890' },
  { code: '+886', nameHu: 'Tajvan', nameEn: 'Taiwan', flag: '🇹🇼', placeholder: '912 345 678' },
  { code: '+60', nameHu: 'Malajzia', nameEn: 'Malaysia', flag: '🇲🇾', placeholder: '12 345 6789' },
  { code: '+66', nameHu: 'Thaiföld', nameEn: 'Thailand', flag: '🇹🇭', placeholder: '81 234 5678' },

  // Közel-Kelet, Kaukázus, Közép-Ázsia
  { code: '+90', nameHu: 'Törökország', nameEn: 'Turkey', flag: '🇹🇷', placeholder: '532 123 4567' },
  { code: '+962', nameHu: 'Jordánia', nameEn: 'Jordan', flag: '🇯🇴', placeholder: '7 9012 3456' },
  { code: '+20', nameHu: 'Egyiptom', nameEn: 'Egypt', flag: '🇪🇬', placeholder: '100 123 4567' },
  { code: '+7', nameHu: 'Kazahsztán / Oroszország', nameEn: 'Kazakhstan / Russia', flag: '🇰🇿', placeholder: '701 123 4567' },
  { code: '+994', nameHu: 'Azerbajdzsán', nameEn: 'Azerbaijan', flag: '🇦🇿', placeholder: '50 123 45 67' },
  { code: '+995', nameHu: 'Grúzia', nameEn: 'Georgia', flag: '🇬🇪', placeholder: '591 12 34 56' },
  { code: '+98', nameHu: 'Irán', nameEn: 'Iran', flag: '🇮🇷', placeholder: '912 345 6789' },
  { code: '+972', nameHu: 'Izrael', nameEn: 'Israel', flag: '🇮🇱', placeholder: '50 123 4567' },
  { code: '+971', nameHu: 'Egyesült Arab Emírségek', nameEn: 'United Arab Emirates', flag: '🇦🇪', placeholder: '50 123 4567' },
  { code: '+966', nameHu: 'Szaúd-Arábia', nameEn: 'Saudi Arabia', flag: '🇸🇦', placeholder: '50 123 4567' },

  // Amerika
  { code: '+1', nameHu: 'USA / Kanada', nameEn: 'USA / Canada', flag: '🇺🇸', placeholder: '202 555 0123' },
  { code: '+55', nameHu: 'Brazília', nameEn: 'Brazil', flag: '🇧🇷', placeholder: '11 91234 5678' },
  { code: '+52', nameHu: 'Mexikó', nameEn: 'Mexico', flag: '🇲🇽', placeholder: '55 1234 5678' },
  { code: '+57', nameHu: 'Kolumbia', nameEn: 'Colombia', flag: '🇨🇴', placeholder: '300 123 4567' },

  // Afrika
  { code: '+234', nameHu: 'Nigéria', nameEn: 'Nigeria', flag: '🇳🇬', placeholder: '802 123 4567' },
  { code: '+233', nameHu: 'Ghána', nameEn: 'Ghana', flag: '🇬🇭', placeholder: '24 123 4567' },
  { code: '+254', nameHu: 'Kenya', nameEn: 'Kenya', flag: '🇰🇪', placeholder: '712 345678' },
  { code: '+27', nameHu: 'Dél-Afrika', nameEn: 'South Africa', flag: '🇿🇦', placeholder: '71 123 4567' },

  // Egyéb / Other
  { code: 'other', nameHu: 'Egyéb ország (+...)', nameEn: 'Other country (+...)', flag: '🌐', placeholder: '+123 456 789' },
];

const parseFullPhone = (fullPhone: string): { prefix: string; localNumber: string } => {
  const trimmed = fullPhone.trim();
  if (!trimmed) return { prefix: '+36', localNumber: '' };

  if (trimmed.startsWith('06')) {
    return { prefix: '+36', localNumber: trimmed.slice(2) };
  }

  if (trimmed.startsWith('+')) {
    // Keresés a listában szereplő prefixek között hossz szerint csökkenő sorrendben
    const sorted = COUNTRY_CODES.filter(c => c.code.startsWith('+')).sort((a, b) => b.code.length - a.code.length);
    const match = sorted.find(c => trimmed.startsWith(c.code));
    if (match) {
      return { prefix: match.code, localNumber: trimmed.slice(match.code.length) };
    }
    // Ha nem szerepel a listában, egyéb nemzetközi számként kezeljük
    return { prefix: 'other', localNumber: trimmed };
  }

  return { prefix: '+36', localNumber: trimmed };
};

const getGenderOptions = () => [
  { value: 'Férfi', label: t('Férfi') },
  { value: 'Nő', label: t('Nő') },
  { value: 'Nem szeretném megadni', label: t('Nem szeretnék válaszolni') },
];

const getLangOptions = () => [
  { value: 'magyar', label: t('Magyar') },
  { value: 'angol', label: t('Angol') },
];

const Step2PersonalData: React.FC<Step2Props> = ({ data, onChange, onNext, onBack }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isEn = getLang() === 'en';

  const { prefix: selectedPrefix, localNumber } = parseFullPhone(data.phone);

  const handlePrefixChange = (newPrefix: string) => {
    if (newPrefix === 'other') {
      if (selectedPrefix !== 'other' && selectedPrefix.startsWith('+')) {
        const cleanLocal = localNumber.replace(/[^\d]/g, '');
        onChange({ phone: `${selectedPrefix}${cleanLocal}` });
      } else {
        onChange({ phone: localNumber.startsWith('+') ? localNumber : `+${localNumber.replace(/[^\d]/g, '')}` });
      }
      return;
    }

    let cleanLocal = localNumber.replace(/[^\d]/g, '');
    if (selectedPrefix === 'other' && localNumber.startsWith('+')) {
      const match = COUNTRY_CODES.filter(c => c.code.startsWith('+')).sort((a, b) => b.code.length - a.code.length).find(c => localNumber.startsWith(c.code));
      if (match) {
        cleanLocal = localNumber.slice(match.code.length).replace(/[^\d]/g, '');
      }
    }

    // Vezető belföldi nulla levágása nemzetközi kódoknál
    if (cleanLocal.startsWith('0')) {
      cleanLocal = cleanLocal.replace(/^0+/, '');
    }

    onChange({ phone: `${newPrefix}${cleanLocal}` });
  };

  const handleLocalPhoneChange = (inputVal: string) => {
    let raw = inputVal.trim();

    // Ha 'other' prefix van kiválasztva
    if (selectedPrefix === 'other') {
      if (!raw.startsWith('+') && raw.length > 0) {
        raw = `+${raw.replace(/[^\d]/g, '')}`;
      } else if (raw.startsWith('+')) {
        raw = `+${raw.slice(1).replace(/[^\d]/g, '')}`;
      }
      onChange({ phone: raw });
      return;
    }

    // Auto-detect pasted + country code
    if (raw.startsWith('+')) {
      onChange({ phone: raw });
      return;
    }

    // Strip leading 06 for HU numbers
    if (selectedPrefix === '+36' && raw.startsWith('06')) {
      raw = raw.slice(2);
    } else if (raw.startsWith('0')) {
      // Nemzetközi számoknál a belföldi kezdő 0 levágása (pl. UK/DE 07... -> 7...)
      raw = raw.replace(/^0+/, '');
    }

    const cleanDigits = raw.replace(/[^\d]/g, '');
    onChange({ phone: `${selectedPrefix}${cleanDigits}` });
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};

    if (!data.lastName.trim()) e.lastName = t('Vezetéknév megadása kötelező.');
    if (!data.firstName.trim()) e.firstName = t('Keresztnév megadása kötelező.');

    // Telefonszám ellenőrzés
    const cleanPhone = data.phone.trim().replace(/[\s-]/g, '');
    if (!cleanPhone || cleanPhone === selectedPrefix || cleanPhone === '+') {
      e.phone = t('Telefonszám megadása kötelező.');
    } else if (cleanPhone.startsWith('+36')) {
      const huLocal = cleanPhone.slice(3);
      if (!/^\d{8,9}$/.test(huLocal)) {
        e.phone = t('Érvényes magyar telefonszámot adj meg (pl. +36 20 123 4567 vagy 06201234567).');
      }
    } else {
      // Nemzetközi ITU E.164 formátum ellenőrzése
      if (!/^\+\d{7,15}$/.test(cleanPhone)) {
        e.phone = t('Érvényes telefonszámot adj meg nemzetközi formátumban (pl. +40 712 345 678).');
      }
    }

    // Születési év ellenőrzés
    const year = parseInt(data.birthYear, 10);
    if (!data.birthYear) {
      e.birthYear = t('Születési év megadása kötelező.');
    } else if (!/^\d{4}$/.test(data.birthYear) || isNaN(year) || year < 1960 || year > 2015) {
      e.birthYear = t('Érvényes születési évet adj meg (1960–2015).');
    }

    if (!data.gender) e.gender = t('Nem megadása kötelező.');
    if (!data.languagePreference) e.languagePreference = t('Nyelvi preferencia kiválasztása kötelező.');

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    const allTouched = { lastName: true, firstName: true, phone: true, birthYear: true, gender: true, languagePreference: true };
    setTouched(allTouched);
    if (validate()) onNext();
  };

  const handleBlur = (field: string) => setTouched(t => ({ ...t, [field]: true }));

  // Dinamikus placeholder kiválasztása
  const currentCountry = COUNTRY_CODES.find(c => c.code === selectedPrefix);
  const dynamicPlaceholder = currentCountry?.placeholder || (selectedPrefix === '+36' ? '20 123 4567' : (selectedPrefix === 'other' ? '+123 456 789' : '123 456 789'));

  return (
    <div className="jw-step-enter" id="jw-reg-step-2">
      <h2 className="jw-step-heading">{t('Személyes adatok')}</h2>

      {/* VEZETÉKNÉV */}
      <div className="jw-field">
        <label className="jw-label" htmlFor="reg-lastname">
          {t('Vezetéknév')} <span className="required">*</span>
        </label>
        <input
          id="reg-lastname"
          type="text"
          className={`jw-input${touched.lastName && errors.lastName ? ' error' : ''}`}
          value={data.lastName}
          autoComplete="family-name"
          onChange={e => onChange({ lastName: e.target.value })}
          onBlur={() => handleBlur('lastName')}
        />
        {touched.lastName && errors.lastName && (
          <div className="jw-input-error">⚠ {errors.lastName}</div>
        )}
      </div>

      {/* KERESZTNÉV */}
      <div className="jw-field">
        <label className="jw-label" htmlFor="reg-firstname">
          {t('Keresztnév')} <span className="required">*</span>
        </label>
        <input
          id="reg-firstname"
          type="text"
          className={`jw-input${touched.firstName && errors.firstName ? ' error' : ''}`}
          value={data.firstName}
          autoComplete="given-name"
          onChange={e => onChange({ firstName: e.target.value })}
          onBlur={() => handleBlur('firstName')}
        />
        {touched.firstName && errors.firstName && (
          <div className="jw-input-error">⚠ {errors.firstName}</div>
        )}
      </div>

      {/* TELEFON CSOPORT ÉS KÓDVÁLASZTÓ */}
      <div className="jw-field">
        <label className="jw-label" htmlFor="reg-phone">
          {t('Telefonszám')} <span className="required">*</span>
        </label>
        <div className="jw-phone-field-wrap">
          <select
            className={`jw-phone-prefix-select${touched.phone && errors.phone ? ' error' : ''}`}
            value={selectedPrefix}
            onChange={e => handlePrefixChange(e.target.value)}
            aria-label={t('Ország hívószáma')}
          >
            {COUNTRY_CODES.map(c => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.code !== 'other' ? c.code : ''} ({isEn ? c.nameEn : c.nameHu})
              </option>
            ))}
          </select>
          <input
            id="reg-phone"
            type="tel"
            className={`jw-input${touched.phone && errors.phone ? ' error' : ''}`}
            value={selectedPrefix === 'other' ? data.phone : localNumber}
            autoComplete="tel-national"
            placeholder={dynamicPlaceholder}
            onChange={e => handleLocalPhoneChange(e.target.value)}
            onBlur={() => handleBlur('phone')}
          />
        </div>
        {touched.phone && errors.phone && (
          <div className="jw-input-error">⚠ {errors.phone}</div>
        )}
      </div>

      {/* SZÜLETÉSI ÉV */}
      <div className="jw-field">
        <label className="jw-label" htmlFor="reg-birthyear">
          {t('Születési év')} <span className="required">*</span>
        </label>
        <input
          id="reg-birthyear"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          className={`jw-input${touched.birthYear && errors.birthYear ? ' error' : ''}`}
          value={data.birthYear}
          placeholder={t('pl. 1998')}
          onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 4);
            onChange({ birthYear: val });
          }}
          onBlur={() => handleBlur('birthYear')}
        />
        {touched.birthYear && errors.birthYear && (
          <div className="jw-input-error">⚠ {errors.birthYear}</div>
        )}
      </div>

      {/* NEM */}
      <div className="jw-field">
        <div className="jw-section-label">
          {t('Nem')} <span className="required" style={{ color: 'var(--color-primary)' }}>*</span>
        </div>
        <div className="jw-radio-group" role="radiogroup" aria-label={t('Nem')}>
          {getGenderOptions().map(opt => (
            <label
              key={opt.value}
              className={`jw-radio-label${data.gender === opt.value ? ' selected' : ''}`}
            >
              <input
                type="radio"
                name="gender"
                value={opt.value}
                checked={data.gender === opt.value}
                onChange={() => {
                  onChange({ gender: opt.value });
                  handleBlur('gender');
                }}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {touched.gender && errors.gender && (
          <div className="jw-input-error">⚠ {errors.gender}</div>
        )}
      </div>

      {/* NYELVI PREFERENCIA */}
      <div className="jw-field">
        <div className="jw-section-label">
          {t('Milyen nyelven szeretnéd megkapni az állásbörzéről az információkat?')} <span className="required" style={{ color: 'var(--color-primary)' }}>*</span>
        </div>
        <div className="jw-radio-group" role="radiogroup" aria-label={t('Nyelvi preferencia')}>
          {getLangOptions().map(opt => (
            <label
              key={opt.value}
              className={`jw-radio-label${data.languagePreference === opt.value ? ' selected' : ''}`}
            >
              <input
                type="radio"
                name="languagePreference"
                value={opt.value}
                checked={data.languagePreference === opt.value}
                onChange={() => {
                  onChange({ languagePreference: opt.value });
                  handleBlur('languagePreference');
                }}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {touched.languagePreference && errors.languagePreference && (
          <div className="jw-input-error">⚠ {errors.languagePreference}</div>
        )}
      </div>

      <div className="jw-required-info">{t('A kötelező mezőket * karakter jelöli.')}</div>

      <div className="jw-btn-row">
        <button type="button" className="jw-btn jw-btn-secondary" onClick={onBack} id="reg-step2-back">
          ← {t('Vissza')}
        </button>
        <button type="button" className="jw-btn jw-btn-primary" onClick={handleNext} id="reg-step2-next">
          {t('Tovább')} →
        </button>
      </div>
    </div>
  );
};

export default Step2PersonalData;
